import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../../main/initializers/axios';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import { toast } from 'react-toastify';
import { IReservationViewModel } from '../Reservations/Reservations';
import { useTranslation } from 'react-i18next';
import '../Reservations/Reservations.css';
import useGetUser from '../../../main/hooks/useGetUser';
import IUser from '../../../main/interfaces/IUser';

interface HotelFolioDto {
  id: number;
  companyId: number;
  hotelId: number;
  reservationId: number;
  guestId: number;
  userId?: number | null;
  status: number;
  openAt: string;
  closeAt?: string | null;
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  totalAmount: number;
  finalInvoiceId?: number | null;
  notes?: string | null;
  hasClosedHistory?: boolean;
  billedReservationPackageIds?: number[];
}

interface HotelFolioRowDto {
  id: number;
  hotelFolioId: number;
  rowType: number;
  referenceType?: string | null;
  referenceId?: number | null;
  serviceDate: string;
  description: string;
  unitPrice: number;
  quantity: number;
  vatRate: number;
  vatAmount: number;
  totalWithoutVat: number;
  totalWithVat: number;
  postedByUserId: number;
  voided: boolean;
  voidedAt?: string | null;
}

interface HotelPaymentDto {
  id: number;
  hotelFolioId: number;
  amount: number;
  method: number;
  paidAt: string;
  referenceNo?: string | null;
  postedByUserId: number;
}

interface ReservationPackageItem {
  id: number;
  hotelPackageId: number;
  quantity: number;
  unitPrice?: number;
  totalPrice: number;
  startDate: string;
  endDate: string;
  packageName?: string;
}

interface Props {
  reservation: IReservationViewModel;
  onClose: () => void;
  onSaved?: () => void;
}

const emptyRowForm = {
  rowType: 5,
  referenceType: '',
  referenceId: '',
  serviceDate: new Date().toISOString().slice(0, 10),
  description: '',
  unitPrice: '',
  quantity: '1',
  vatRate: '0',
};

const emptyPaymentForm = {
  amount: '',
  method: '1',
  referenceNo: '',
  paidAt: new Date().toISOString().slice(0, 16),
};

const getFolioStatusLabel = (t: any, status?: number | null) => {
  if (status === 1) return t('hotelBilling.status.open');
  if (status === 2) return t('hotelBilling.status.closed');
  if (status === 3) return t('hotelBilling.status.cancelled');
  return '-';
};

const getHotelPaymentMethodLabel = (t: any, method?: number | null) => {
  if (method === 1) return t('hotelBilling.paymentMethods.cash');
  if (method === 2) return t('hotelBilling.paymentMethods.card');
  if (method === 3) return t('hotelBilling.paymentMethods.bank');
  if (method === 4) return t('hotelBilling.paymentMethods.online');
  return '-';
};

const HotelBillingModal = ({ reservation, onClose, onSaved }: Props) => {
  const { t } = useTranslation();
  const currentUser = useGetUser() as IUser | null;
  const [hotelFolio, setHotelFolio] = useState<HotelFolioDto | null>(null);
  const [hotelFolioRows, setHotelFolioRows] = useState<HotelFolioRowDto[]>([]);
  const [hotelPayments, setHotelPayments] = useState<HotelPaymentDto[]>([]);
  const [reservationPackages, setReservationPackages] = useState<ReservationPackageItem[]>([]);
  const [historicalBilledPackageIds, setHistoricalBilledPackageIds] = useState<number[]>([]);
  const [hotelBalance, setHotelBalance] = useState<number>(0);
  const [hotelInvoiceId, setHotelInvoiceId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [isCreatingFolio, setIsCreatingFolio] = useState(false);
  const [isSavingRow, setIsSavingRow] = useState(false);
  const [isSavingPayment, setIsSavingPayment] = useState(false);
  const [deletingRowId, setDeletingRowId] = useState<number | null>(null);
  const [isClosingFolio, setIsClosingFolio] = useState(false);
  const [isPaymentHistoryOpen, setIsPaymentHistoryOpen] = useState(false);
  const [hotelRowForm, setHotelRowForm] = useState(emptyRowForm);
  const [hotelPaymentForm, setHotelPaymentForm] = useState(emptyPaymentForm);
  const rowTypeOptions = useMemo(
    () => [
      { value: 1, label: t('hotelBilling.rowTypes.roomCharge') },
      { value: 2, label: t('hotelBilling.rowTypes.packageCharge') },
      { value: 3, label: t('hotelBilling.rowTypes.parkingCharge') },
      { value: 4, label: t('hotelBilling.rowTypes.productCharge') },
      { value: 5, label: t('hotelBilling.rowTypes.manualCharge') },
      { value: 6, label: t('hotelBilling.rowTypes.discount') },
      { value: 7, label: t('hotelBilling.rowTypes.tax') },
    ],
    [t],
  );

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [],
  );

  const stayNights = useMemo(() => {
    if (!reservation.checkInDate || !reservation.checkOutDate) {
      return 0;
    }

    const start = new Date(reservation.checkInDate);
    const end = new Date(reservation.checkOutDate);
    const diffInMs = end.getTime() - start.getTime();

    if (Number.isNaN(diffInMs) || diffInMs <= 0) {
      return 0;
    }

    return Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
  }, [reservation.checkInDate, reservation.checkOutDate]);

  const reservationPackagesTotal = useMemo(
    () => reservationPackages.reduce((sum, item) => sum + Number(item.totalPrice ?? 0), 0),
    [reservationPackages],
  );

  const estimatedRoomCharge = useMemo(() => {
    const reservationTotal = Number(reservation.lockedPriceTotal ?? 0);
    return Math.max(0, reservationTotal - reservationPackagesTotal);
  }, [reservation.lockedPriceTotal, reservationPackagesTotal]);

  const hotelComputedTotals = useMemo(() => {
    const activeRows = hotelFolioRows.filter((row) => !row.voided);
    const subtotal = activeRows.filter((row) => row.rowType !== 6).reduce((sum, row) => sum + Number(row.totalWithoutVat ?? 0), 0);
    const discountAmount = Math.abs(activeRows.filter((row) => row.rowType === 6).reduce((sum, row) => sum + Number(row.totalWithoutVat ?? 0), 0));
    const taxAmount = activeRows.reduce((sum, row) => sum + Number(row.vatAmount ?? 0), 0);
    const totalAmount = activeRows.reduce((sum, row) => sum + Number(row.totalWithVat ?? 0), 0);

    return {
      subtotal,
      discountAmount,
      taxAmount,
      totalAmount,
    };
  }, [hotelFolioRows]);

  const hotelOutstandingBalance = useMemo(() => Math.max(0, Number(hotelBalance ?? 0)), [hotelBalance]);
  const canRecordHotelPayment = hotelFolio?.status === 1 && hotelOutstandingBalance > 0;
  const isOpenedByCurrentUser = useMemo(() => {
    if (!hotelFolio?.userId) {
      return true;
    }

    return Number(hotelFolio.userId) === Number(currentUser?.id ?? 0);
  }, [currentUser?.id, hotelFolio?.userId]);
  const canCloseHotelFolio =
    hotelFolio?.status === 1 && hotelFolioRows.length > 0 && hotelOutstandingBalance === 0 && isOpenedByCurrentUser;
  const currentFolioBilledPackageIds = useMemo(
    () =>
      hotelFolioRows
        .filter((row) => !row.voided && row.referenceType === 'ReservationPackage' && row.referenceId != null)
        .map((row) => Number(row.referenceId))
        .filter((id, index, ids) => !Number.isNaN(id) && ids.indexOf(id) === index),
    [hotelFolioRows],
  );
  const visibleReservationPackages = useMemo(
    () => reservationPackages.filter((item) => !historicalBilledPackageIds.includes(item.id) && !currentFolioBilledPackageIds.includes(item.id)),
    [currentFolioBilledPackageIds, historicalBilledPackageIds, reservationPackages],
  );
  const hasHistoricalHotelInvoice = historicalBilledPackageIds.length > 0 || hotelFolio?.hasClosedHistory || hotelFolio?.status === 2;

  const resetHotelBillingState = () => {
    setHotelFolio(null);
    setHotelFolioRows([]);
    setHotelPayments([]);
    setHistoricalBilledPackageIds([]);
    setHotelBalance(0);
    setHotelInvoiceId(null);
    setIsPaymentHistoryOpen(false);
  };

  const resolveHotelFolio = async () => {
    const response = await axiosInstance.get(ApiConstants.hotelFolio.byReservation, {
      params: { reservationId: reservation.id },
    });

    const hotelFolioResponse = response.data ?? null;
    setHotelFolio(hotelFolioResponse);
    setHotelInvoiceId(hotelFolioResponse?.finalInvoiceId ?? null);
    setHistoricalBilledPackageIds(
      Array.isArray(hotelFolioResponse?.billedReservationPackageIds) ? hotelFolioResponse.billedReservationPackageIds : [],
    );
    return hotelFolioResponse;
  };

  const loadReservationPackages = async () => {
    const packagesResponse = await axiosInstance.get(ApiConstants.reservation.listPackages, {
      params: { reservationId: reservation.id },
    });

    setReservationPackages(Array.isArray(packagesResponse.data) ? packagesResponse.data : []);
    return Array.isArray(packagesResponse.data) ? packagesResponse.data : [];
  };

  const loadHotelFolioDetails = async (folioId: number) => {
    const [rowsResponse, balanceResponse] = await Promise.all([
      axiosInstance.get(ApiConstants.hotelFolio.rows, {
        params: { hotelFolioId: folioId },
      }),
      axiosInstance.get(ApiConstants.hotelFolio.balance, {
        params: { folioId },
      }),
    ]);

    const rows = Array.isArray(rowsResponse.data) ? rowsResponse.data : [];
    const balance = Number(balanceResponse.data ?? 0);

    setHotelFolioRows(rows);
    setHotelBalance(balance);

    return { rows, balance };
  };

  const loadHotelPayments = async () => {
    setIsLoadingPayments(true);
    try {
      const paymentsResponse = await axiosInstance.get(ApiConstants.hotelFolio.paymentsByReservation, {
        params: { reservationId: reservation.id },
      });

      setHotelPayments(Array.isArray(paymentsResponse.data) ? paymentsResponse.data : []);
    } catch {
      toast.error(t('hotelBilling.messages.loadPaymentsError'));
    } finally {
      setIsLoadingPayments(false);
    }
  };

  const extractBilledReservationPackageIds = (rows: HotelFolioRowDto[]) =>
    rows
      .filter((row) => !row.voided && row.referenceType === 'ReservationPackage' && row.referenceId != null)
      .map((row) => Number(row.referenceId))
      .filter((id, index, ids) => !Number.isNaN(id) && ids.indexOf(id) === index);

  const loadHotelBilling = async () => {
    setIsLoading(true);

    try {
      const loadedReservationPackages = await loadReservationPackages();
      let hotelFolioResponse = await resolveHotelFolio();

      if (!hotelFolioResponse?.id) {
        resetHotelBillingState();
        return;
      }

      let { rows } = await loadHotelFolioDetails(hotelFolioResponse.id);
      await loadHotelPayments();
      let billedReservationPackageIds = extractBilledReservationPackageIds(rows);
      const hasUnbilledReservationPackages = loadedReservationPackages.some(
        (reservationPackage) => !billedReservationPackageIds.includes(reservationPackage.id),
      );

      if (hotelFolioResponse.status !== 1 && hasUnbilledReservationPackages) {
        await axiosInstance.post(ApiConstants.hotelFolio.create, {
          reservationId: reservation.id,
          notes: `Supplemental hotel folio for reservation #${reservation.id}`,
        });

        setHistoricalBilledPackageIds(billedReservationPackageIds);
        hotelFolioResponse = await resolveHotelFolio();

        if (hotelFolioResponse?.id) {
          const nextDetails = await loadHotelFolioDetails(hotelFolioResponse.id);
          await loadHotelPayments();
          rows = nextDetails.rows;
          billedReservationPackageIds = extractBilledReservationPackageIds(rows);
        }
      } else {
        setHistoricalBilledPackageIds(hotelFolioResponse.status === 2 ? billedReservationPackageIds : []);
      }
    } catch {
      toast.error(t('hotelBilling.messages.loadBillingError'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadHotelBilling();
  }, [reservation.id]);

  const handleCreateFolio = async () => {
    setIsCreatingFolio(true);

    try {
      await axiosInstance.post(ApiConstants.hotelFolio.create, {
        reservationId: reservation.id,
        notes: `Hotel folio for reservation #${reservation.id}`,
      });

      toast.success(t('hotelBilling.messages.folioCreated'));
      await loadHotelBilling();
      onSaved?.();
    } catch {
      toast.error(t('hotelBilling.messages.createFolioError'));
    } finally {
      setIsCreatingFolio(false);
    }
  };

  const handleDeleteHotelRow = async (rowId: number) => {
    if (!hotelFolio?.id || rowId <= 0 || hotelFolio.status !== 1) {
      return;
    }

    setDeletingRowId(rowId);

    try {
      await axiosInstance.delete(ApiConstants.hotelFolio.deleteRow, {
        params: { rowId },
      });

      await loadHotelFolioDetails(hotelFolio.id);
      toast.success(t('hotelBilling.messages.rowDeleted'));
      onSaved?.();
    } catch {
      toast.error(t('hotelBilling.messages.deleteRowError'));
    } finally {
      setDeletingRowId(null);
    }
  };

  const useReservationStayValues = () => {
    setHotelRowForm((current) => ({
      ...current,
      rowType: 1,
      referenceType: 'Reservation',
      referenceId: String(reservation.id),
      serviceDate: reservation.checkInDate?.split('T')[0] ?? current.serviceDate,
      description: t('hotelBilling.stayChargeDescription', {
        room: reservation.roomNumber ?? `#${reservation.roomId}`,
      }),
      unitPrice: estimatedRoomCharge ? estimatedRoomCharge.toFixed(2) : '',
      quantity: '1',
    }));
  };

  const applyReservationPackageValues = (reservationPackage: ReservationPackageItem) => {
    setHotelRowForm((current) => ({
      ...current,
      rowType: 2,
      referenceType: 'ReservationPackage',
      referenceId: String(reservationPackage.id),
      serviceDate: reservationPackage.startDate?.split('T')[0] ?? current.serviceDate,
      description: reservationPackage.packageName || t('hotelBilling.packageFallbackName', { id: reservationPackage.hotelPackageId }),
      unitPrice: Number(reservationPackage.totalPrice ?? 0).toFixed(2),
      quantity: '1',
    }));
  };

  const handleHotelRowSave = async () => {
    if (!hotelFolio?.id) {
      toast.error(t('hotelBilling.messages.folioNotReady'));
      return;
    }

    if (!hotelRowForm.description.trim()) {
      toast.error(t('hotelBilling.messages.descriptionRequired'));
      return;
    }

    setIsSavingRow(true);
    try {
      await axiosInstance.post(ApiConstants.hotelFolio.saveRow, {
        id: 0,
        hotelFolioId: hotelFolio.id,
        rowType: Number(hotelRowForm.rowType),
        referenceType: hotelRowForm.referenceType || null,
        referenceId: hotelRowForm.referenceId ? Number(hotelRowForm.referenceId) : null,
        serviceDate: new Date(hotelRowForm.serviceDate).toISOString(),
        description: hotelRowForm.description.trim(),
        unitPrice: Number(hotelRowForm.unitPrice),
        quantity: Number(hotelRowForm.quantity),
        vatRate: Number(hotelRowForm.vatRate),
      });

      setHotelRowForm(emptyRowForm);
      toast.success(t('hotelBilling.messages.rowSaved'));
      await loadHotelBilling();
      onSaved?.();
    } catch {
      toast.error(t('hotelBilling.messages.saveRowError'));
    } finally {
      setIsSavingRow(false);
    }
  };

  const handleHotelPaymentSave = async () => {
    if (!hotelFolio?.id) {
      toast.error(t('hotelBilling.messages.folioNotReady'));
      return;
    }

    if (!canRecordHotelPayment) {
      toast.error(t('hotelBilling.messages.noOutstandingBalance'));
      return;
    }

    const parsedAmount = Number(hotelPaymentForm.amount);

    if (!hotelPaymentForm.amount || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error(t('hotelBilling.messages.paymentAmountPositive'));
      return;
    }

    if (parsedAmount > hotelOutstandingBalance) {
      toast.error(t('hotelBilling.messages.paymentAmountTooHigh'));
      return;
    }

    setIsSavingPayment(true);
    try {
      await axiosInstance.post(ApiConstants.hotelFolio.payment, {
        hotelFolioId: hotelFolio.id,
        amount: parsedAmount,
        method: Number(hotelPaymentForm.method),
        referenceNo: hotelPaymentForm.referenceNo || null,
        paidAt: hotelPaymentForm.paidAt ? new Date(hotelPaymentForm.paidAt).toISOString() : null,
      });

      setHotelPaymentForm(emptyPaymentForm);
      toast.success(t('hotelBilling.messages.paymentRecorded'));
      await loadHotelBilling();
      onSaved?.();
    } catch {
      toast.error(t('hotelBilling.messages.recordPaymentError'));
    } finally {
      setIsSavingPayment(false);
    }
  };

  const handleHotelFolioClose = async () => {
    if (!hotelFolio?.id) {
      toast.error(t('hotelBilling.messages.folioNotReady'));
      return;
    }

    if (!isOpenedByCurrentUser) {
      toast.error(
        t('hotelBilling.messages.folioCloseOnlyOpeningUser', {
          defaultValue: 'Only the user who opened this hotel folio can close it.',
        }),
      );
      return;
    }

    setIsClosingFolio(true);
    try {
      const response = await axiosInstance.post(ApiConstants.hotelFolio.close, {
        hotelFolioId: hotelFolio.id,
      });

      const createdHotelInvoiceId = Number(response.data?.invoiceId ?? 0);
      const printQueued = Boolean(response.data?.printQueued);

      setHotelInvoiceId(createdHotelInvoiceId);

      if (printQueued) {
        toast.success(t('hotelBilling.messages.folioClosedAndQueued'));
      } else {
        toast.success(t('hotelBilling.messages.folioClosed'));
        toast.info(t('hotelBilling.messages.printInvoiceNoPrinter'));
      }

      await loadHotelBilling();
      onSaved?.();
    } catch {
      toast.error(t('hotelBilling.messages.closeFolioError'));
    } finally {
      setIsClosingFolio(false);
    }
  };

  const handlePrintHotelInvoice = async () => {
    const targetInvoiceId = Number(hotelInvoiceId ?? hotelFolio?.finalInvoiceId ?? 0);

    if (!targetInvoiceId) {
      toast.error(t('hotelBilling.messages.folioNotReady'));
      return;
    }

    try {
      const response = await axiosInstance.post(ApiConstants.hotelFolio.printInvoice, {
        hotelInvoiceId: targetInvoiceId,
      });
      const queued = Boolean(response.data);

      if (queued) {
        toast.success(t('hotelBilling.messages.printInvoiceQueued'));
        return;
      }

      toast.error(t('hotelBilling.messages.printInvoiceNoPrinter'));
    } catch {
      toast.error(t('hotelBilling.messages.printInvoiceError'));
    }
  };

  return (
    <div className="reservation-modal reservation-packages-overlay">
      <div className="reservation-modal-content reservation-packages-modal hotel-billing-modal">
        <div className="reservation-modal-top hotel-billing-modal-top">
          <div>
            <span className="reservation-kicker">{t('hotelBilling.title')}</span>
            <h3>{t('hotelBilling.folioTitle')}</h3>
            <p>{t('hotelBilling.subtitle', { id: reservation.id })}</p>
          </div>
        </div>

        <div className="reservation-summary hotel-billing-summary">
          <div className="reservation-summary-item">
            <span>{t('hotelBilling.labels.room')}</span>
            <strong>{reservation.roomNumber ?? (reservation.roomId ? `#${reservation.roomId}` : '-')}</strong>
          </div>
          <div className="reservation-summary-item">
            <span>{t('hotelBilling.labels.guest')}</span>
            <strong>{reservation.guestName || '-'}</strong>
          </div>
          <div className="reservation-summary-item">
            <span>{t('hotelBilling.labels.stay')}</span>
            <strong>
              {reservation.checkInDate?.split('T')[0] ?? '-'} to {reservation.checkOutDate?.split('T')[0] ?? '-'}
            </strong>
          </div>
          <div className="reservation-summary-item">
            <span>{t('hotelBilling.labels.nights')}</span>
            <strong>{stayNights || '-'}</strong>
          </div>
          <div className="reservation-summary-item">
            <span>{t('hotelBilling.labels.status')}</span>
            <strong>{getFolioStatusLabel(t, hotelFolio?.status)}</strong>
          </div>
          <div className="reservation-summary-item">
            <span>{t('hotelBilling.labels.reservationTotal')}</span>
            <strong>{formatter.format(Number(reservation.lockedPriceTotal ?? 0))}</strong>
          </div>
          <div className="reservation-summary-item">
            <span>{t('hotelBilling.labels.packagesTotal')}</span>
            <strong>{formatter.format(reservationPackagesTotal)}</strong>
          </div>
          <div className="reservation-summary-item">
            <span>{t('hotelBilling.labels.estimatedRoomCharge')}</span>
            <strong>{formatter.format(estimatedRoomCharge)}</strong>
          </div>
          <div className="reservation-summary-item reservation-summary-item-price">
            <span>{t('hotelBilling.labels.subtotal')}</span>
            <strong>{formatter.format(hotelComputedTotals.subtotal)}</strong>
          </div>
          <div className="reservation-summary-item">
            <span>{t('hotelBilling.labels.tax')}</span>
            <strong>{formatter.format(hotelComputedTotals.taxAmount)}</strong>
          </div>
          <div className="reservation-summary-item">
            <span>{t('hotelBilling.labels.discount')}</span>
            <strong>{formatter.format(hotelComputedTotals.discountAmount)}</strong>
          </div>
          <div className="reservation-summary-item reservation-summary-item-total">
            <span>{t('hotelBilling.labels.total')}</span>
            <strong>{formatter.format(hotelComputedTotals.totalAmount)}</strong>
          </div>
          <div className="reservation-summary-item">
            <span>{t('hotelBilling.labels.balance')}</span>
            <strong>{formatter.format(hotelBalance)}</strong>
          </div>
          <div className="reservation-summary-item">
            <span>Hotel invoice</span>
            <strong>{hotelInvoiceId ?? hotelFolio?.finalInvoiceId ?? '-'}</strong>
          </div>
        </div>

        {isLoading ? (
          <div className="hotel-empty">Loading hotel billing...</div>
        ) : !hotelFolio ? (
          <div className="hotel-empty hotel-billing-empty-state">
            <div className="hotel-billing-empty-copy">
              <strong>No hotel folio yet</strong>
              <span>Create a folio for this reservation before adding rows, payments, and checkout details.</span>
            </div>
            <button type="button" className="hotel-action-btn hotel-billing-btn" onClick={handleCreateFolio} disabled={isCreatingFolio}>
              {isCreatingFolio ? 'Creating...' : 'Create folio'}
            </button>
          </div>
        ) : (
          <div className="reservation-packages-shell hotel-billing-shell">
            <div className="reservation-package-create-card hotel-billing-panel hotel-billing-panel-primary">
              <div className="reservation-package-card-header hotel-billing-header-grid">
                <div>
                  <h4>{t('hotelBilling.rowsTitle')}</h4>
                  <p>{t('hotelBilling.rowsSubtitle')}</p>
                </div>
                <div className="hotel-billing-header-meta">
                  <span className="reservation-package-tag">{t('hotelBilling.activeRows', { count: hotelFolioRows.length })}</span>
                  <span className="reservation-package-tag">{t('hotelBilling.folioTag', { status: getFolioStatusLabel(t, hotelFolio.status) })}</span>
                </div>
              </div>

              <div className="hotel-billing-source-grid">
                {!hasHistoricalHotelInvoice && (
                  <div className="hotel-billing-source-card">
                    <div className="hotel-billing-source-copy">
                      <strong>{t('hotelBilling.reservationStay')}</strong>
                      <span>
                        {reservation.roomNumber ?? `#${reservation.roomId}`} | {t('hotelBilling.nightsCount', { count: stayNights })} |{' '}
                        {t('hotelBilling.labels.estimatedRoomCharge')} {formatter.format(estimatedRoomCharge)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="hotel-action-btn hotel-secondary-btn"
                      onClick={useReservationStayValues}
                      disabled={hotelFolio?.status !== 1}
                    >
                      {t('hotelBilling.actions.useStay')}
                    </button>
                  </div>
                )}

                {visibleReservationPackages.map((reservationPackage) => (
                  <div key={reservationPackage.id} className="hotel-billing-source-card">
                    <div className="hotel-billing-source-copy">
                      <strong>{reservationPackage.packageName || t('hotelBilling.packageFallbackName', { id: reservationPackage.hotelPackageId })}</strong>
                      <span>
                        {t('hotelBilling.packageRowSummary', {
                          quantity: formatter.format(Number(reservationPackage.quantity ?? 0)),
                          total: formatter.format(Number(reservationPackage.totalPrice ?? 0)),
                        })}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="hotel-action-btn hotel-secondary-btn"
                      onClick={() => applyReservationPackageValues(reservationPackage)}
                      disabled={hotelFolio?.status !== 1}
                    >
                      {t('hotelBilling.actions.usePackage')}
                    </button>
                  </div>
                ))}

                {visibleReservationPackages.length === 0 && (
                  <div className="hotel-billing-source-card hotel-billing-source-empty">
                    <div className="hotel-billing-source-copy">
                      <strong>{t('hotelBilling.noPackagesTitle')}</strong>
                      <span>{t('hotelBilling.noPackagesText')}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="reservation-package-form-grid hotel-billing-form-grid">
                <div className="modal-field">
                  <label>{t('hotelBilling.fields.rowType')}</label>
                  <select
                    value={hotelRowForm.rowType}
                    onChange={(event) =>
                      setHotelRowForm((current) => ({
                        ...current,
                        rowType: Number(event.target.value),
                      }))
                    }
                  >
                    {rowTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="modal-field">
                  <label>{t('hotelBilling.fields.serviceDate')}</label>
                  <input
                    type="date"
                    value={hotelRowForm.serviceDate}
                    onChange={(event) =>
                      setHotelRowForm((current) => ({
                        ...current,
                        serviceDate: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="modal-field">
                  <label>{t('hotelBilling.fields.unitPrice')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={hotelRowForm.unitPrice}
                    onChange={(event) =>
                      setHotelRowForm((current) => ({
                        ...current,
                        unitPrice: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="modal-field">
                  <label>{t('hotelBilling.fields.quantity')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={hotelRowForm.quantity}
                    onChange={(event) =>
                      setHotelRowForm((current) => ({
                        ...current,
                        quantity: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="modal-field">
                  <label>{t('hotelBilling.fields.vatRate')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={hotelRowForm.vatRate}
                    onChange={(event) =>
                      setHotelRowForm((current) => ({
                        ...current,
                        vatRate: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="modal-field">
                  <label>{t('hotelBilling.fields.referenceType')}</label>
                  <input
                    value={hotelRowForm.referenceType}
                    onChange={(event) =>
                      setHotelRowForm((current) => ({
                        ...current,
                        referenceType: event.target.value,
                      }))
                    }
                    placeholder={t('hotelBilling.placeholders.referenceType')}
                  />
                </div>

                <div className="modal-field">
                  <label>{t('hotelBilling.fields.referenceId')}</label>
                  <input
                    type="number"
                    value={hotelRowForm.referenceId}
                    onChange={(event) =>
                      setHotelRowForm((current) => ({
                        ...current,
                        referenceId: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="modal-field hotel-billing-description-field">
                  <label>{t('hotelBilling.fields.description')}</label>
                  <input
                    value={hotelRowForm.description}
                    onChange={(event) =>
                      setHotelRowForm((current) => ({
                        ...current,
                        description: event.target.value,
                      }))
                    }
                    placeholder={t('hotelBilling.placeholders.description')}
                  />
                </div>

                <div className="modal-field hotel-billing-add-row-field">
                  <label>&nbsp;</label>
                  <button
                    className="hotel-action-btn hotel-secondary-btn"
                    onClick={handleHotelRowSave}
                    disabled={isSavingRow || hotelFolio?.status !== 1}
                  >
                    {isSavingRow ? t('hotelBilling.actions.saving') : t('hotelBilling.actions.addHotelRow')}
                  </button>
                </div>
              </div>

              <div className="hotelTableWrap hotel-billing-table-wrap">
                <table className="hotelDataTable hotel-table">
                  <thead>
                    <tr>
                      <th>{t('hotelBilling.table.type')}</th>
                      <th>{t('hotelBilling.table.date')}</th>
                      <th>{t('hotelBilling.fields.description')}</th>
                      <th>{t('hotelBilling.table.qty')}</th>
                      <th>{t('hotelBilling.table.unit')}</th>
                      <th>{t('hotelBilling.table.vat')}</th>
                      <th>{t('hotelBilling.labels.total')}</th>
                      <th aria-label={t('hotelBilling.actions.deleteRow')}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {hotelFolioRows.map((hotelRow) => (
                      <tr key={hotelRow.id}>
                        <td>{rowTypeOptions.find((item) => item.value === hotelRow.rowType)?.label ?? `#${hotelRow.rowType}`}</td>
                        <td>{hotelRow.serviceDate ? new Date(hotelRow.serviceDate).toLocaleDateString() : '-'}</td>
                        <td>{hotelRow.description}</td>
                        <td>{formatter.format(hotelRow.quantity)}</td>
                        <td>{formatter.format(hotelRow.unitPrice)}</td>
                        <td>{formatter.format(hotelRow.vatRate)}</td>
                        <td>{formatter.format(hotelRow.totalWithVat)}</td>
                        <td className="hotel-billing-row-action-cell">
                          {hotelFolio?.status === 1 && (
                            <button
                              type="button"
                              className="hotel-billing-row-delete-btn"
                              onClick={() => void handleDeleteHotelRow(hotelRow.id)}
                              disabled={deletingRowId === hotelRow.id}
                              title={t('hotelBilling.actions.deleteRow')}
                              aria-label={t('hotelBilling.actions.deleteRow')}
                            >
                              {deletingRowId === hotelRow.id ? '...' : 'X'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {hotelFolioRows.length === 0 && (
                      <tr>
                        <td colSpan={8} className="hotelEmptyState">
                          {t('hotelBilling.noRows')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="reservation-package-create-card hotel-billing-panel">
              <div className="reservation-package-card-header hotel-billing-header-grid">
                <div>
                  <h4>{t('hotelBilling.paymentsTitle')}</h4>
                  <p>{t('hotelBilling.paymentsSubtitle')}</p>
                </div>
                <div className="hotel-billing-header-meta">
                  <span className="reservation-package-tag">{t('hotelBilling.balanceTag', { value: formatter.format(hotelBalance) })}</span>
                  <span className="reservation-package-tag">
                    {t('hotelBilling.invoiceTag', { value: hotelInvoiceId ?? hotelFolio.finalInvoiceId ?? '-' })}
                  </span>
                </div>
              </div>

              <div className="reservation-package-form-grid hotel-billing-form-grid hotel-billing-payment-grid">
                <div className="modal-field">
                  <label>{t('hotelBilling.fields.amount')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={hotelPaymentForm.amount}
                    disabled={!canRecordHotelPayment}
                    onChange={(event) =>
                      setHotelPaymentForm((current) => ({
                        ...current,
                        amount: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="modal-field">
                  <label>{t('hotelBilling.fields.method')}</label>
                  <select
                    value={hotelPaymentForm.method}
                    disabled={!canRecordHotelPayment}
                    onChange={(event) =>
                      setHotelPaymentForm((current) => ({
                        ...current,
                        method: event.target.value,
                      }))
                    }
                  >
                    <option value="1">{t('hotelBilling.paymentMethods.cash')}</option>
                    <option value="2">{t('hotelBilling.paymentMethods.card')}</option>
                    <option value="3">{t('hotelBilling.paymentMethods.bank')}</option>
                    <option value="4">{t('hotelBilling.paymentMethods.online')}</option>
                  </select>
                </div>

                <div className="modal-field">
                  <label>{t('hotelBilling.fields.paidAt')}</label>
                  <input
                    type="datetime-local"
                    value={hotelPaymentForm.paidAt}
                    disabled={!canRecordHotelPayment}
                    onChange={(event) =>
                      setHotelPaymentForm((current) => ({
                        ...current,
                        paidAt: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="modal-field">
                  <label>{t('hotelBilling.fields.referenceNo')}</label>
                  <input
                    value={hotelPaymentForm.referenceNo}
                    disabled={!canRecordHotelPayment}
                    onChange={(event) =>
                      setHotelPaymentForm((current) => ({
                        ...current,
                        referenceNo: event.target.value,
                      }))
                    }
                    placeholder={t('hotelBilling.placeholders.referenceNo')}
                  />
                </div>
              </div>

              <div className="reservation-package-card-actions hotel-billing-footer-actions">
                <div className="hotel-billing-footer-actions-left">
                  <button
                    className="hotel-action-btn hotel-secondary-btn"
                    onClick={handleHotelPaymentSave}
                    disabled={isSavingPayment || !canRecordHotelPayment}
                  >
                    {isSavingPayment ? t('hotelBilling.actions.saving') : t('hotelBilling.actions.recordPayment')}
                  </button>
                  <button
                    type="button"
                    className="hotel-action-btn hotel-secondary-btn"
                    onClick={() => setIsPaymentHistoryOpen(true)}
                    disabled={!hotelFolio || isLoadingPayments}
                  >
                    {isLoadingPayments ? t('hotelBilling.actions.loadingPayments') : t('hotelBilling.actions.paymentHistory')}
                  </button>
                </div>
                <div className="hotel-billing-footer-actions-right">
                  {canCloseHotelFolio && (
                    <button className="hotel-action-btn hotel-checkout-btn" onClick={handleHotelFolioClose} disabled={isClosingFolio}>
                      {isClosingFolio ? t('hotelBilling.actions.closing') : t('hotelBilling.actions.closeFolio')}
                    </button>
                  )}
                </div>
              </div>

              {!canRecordHotelPayment && <p className="reservation-package-helper hotel-billing-helper">{t('hotelBilling.paymentDisabled')}</p>}
              {hotelFolio?.status === 1 && !isOpenedByCurrentUser && (
                <p className="reservation-package-helper hotel-billing-helper">
                  {t('hotelBilling.messages.folioCloseOnlyOpeningUser', {
                    defaultValue: 'Only the user who opened this hotel folio can close it.',
                  })}
                </p>
              )}
            </div>

            {hotelInvoiceId && (
              <div className="reservation-package-create-card hotel-billing-panel hotel-invoice-preview">
                <div className="reservation-package-card-header hotel-billing-header-grid">
                  <div>
                    <h4>{t('hotelBilling.invoicePreviewTitle')}</h4>
                    <p>{t('hotelBilling.invoicePreviewSubtitle')}</p>
                  </div>
                  <span className="reservation-package-tag">{t('hotelBilling.invoiceNumber', { id: hotelInvoiceId })}</span>
                </div>

                <div className="reservation-package-card-actions">
                  <button type="button" className="hotel-action-btn hotel-checkout-btn" onClick={() => void handlePrintHotelInvoice()}>
                    {t('hotelBilling.actions.printInvoice')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="reservation-package-card-actions hotel-billing-page-actions">
          <button
            type="button"
            className="hotel-action-btn hotel-secondary-btn"
            onClick={() => void loadHotelBilling()}
            disabled={!hotelFolio || isLoading}
          >
            {isLoading ? t('hotelBilling.actions.refreshing') : t('hotelBilling.actions.refresh')}
          </button>
          <button type="button" className="hotel-action-btn hotel-secondary-btn" onClick={onClose}>
            {t('generic.close')}
          </button>
        </div>
      </div>

      {isPaymentHistoryOpen && (
        <div className="reservation-modal hotel-payment-history-overlay" onClick={() => setIsPaymentHistoryOpen(false)}>
          <div className="reservation-modal-content hotel-payment-history-modal" onClick={(event) => event.stopPropagation()}>
            <div className="reservation-modal-top">
              <div>
                <span className="reservation-kicker">{t('hotelBilling.title')}</span>
                <h3>{t('hotelBilling.paymentHistoryTitle')}</h3>
                <p>{t('hotelBilling.paymentHistorySubtitle')}</p>
              </div>
            </div>

            <div className="hotel-payment-history-list">
              {hotelPayments.length === 0 ? (
                <div className="hotel-empty">{t('hotelBilling.noPayments')}</div>
              ) : (
                hotelPayments.map((payment, index) => (
                  <div key={payment.id} className="hotel-payment-history-card">
                    <div className="hotel-payment-history-row">
                      <span>{t('hotelBilling.fields.installment')}</span>
                      <strong>#{index + 1}</strong>
                    </div>
                    <div className="hotel-payment-history-row">
                      <span>{t('hotelBilling.fields.amount')}</span>
                      <strong>{formatter.format(Number(payment.amount ?? 0))}</strong>
                    </div>
                    <div className="hotel-payment-history-row">
                      <span>{t('hotelBilling.fields.method')}</span>
                      <strong>{getHotelPaymentMethodLabel(t, payment.method)}</strong>
                    </div>
                    <div className="hotel-payment-history-row">
                      <span>{t('hotelBilling.fields.paidAt')}</span>
                      <strong>{payment.paidAt ? new Date(payment.paidAt).toLocaleString() : '-'}</strong>
                    </div>
                    <div className="hotel-payment-history-row">
                      <span>{t('hotelBilling.fields.reference')}</span>
                      <strong>{payment.referenceNo || '-'}</strong>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="modal-actions">
              <button type="button" className="hotel-action-btn hotel-secondary-btn" onClick={() => setIsPaymentHistoryOpen(false)}>
                {t('generic.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelBillingModal;
